import { Injectable } from '@angular/core';
import { isNonEmptyString } from '@fmnts/core';
import {
  AbstractGraphNodeBuilder,
  GraphBuilderContext,
  TemplateBuilder,
  interpolateTemplate,
  templateExpressionRegex,
} from '@fmnts/declarative-forms/core';
import {
  EMPTY,
  OperatorFunction,
  catchError,
  filter,
  of,
  switchMap,
} from 'rxjs';

export enum TemplateOperatorName {
  Interpolate = 'template.interpolate',
}

/**
 * Builder for `TemplateBuilder` related graph nodes.
 */
@Injectable()
export class TemplateNodeBuilder extends AbstractGraphNodeBuilder<
  never,
  TemplateOperatorName
> {
  constructor() {
    super();

    // Add operators
    this.addOperator(TemplateOperatorName.Interpolate, (_, ctx) =>
      this.interpolate(ctx),
    );
  }

  /**
   * @returns
   * An `Observable` that emits interpolated templates.
   */
  private interpolate(
    ctx: GraphBuilderContext,
  ): OperatorFunction<unknown, string> {
    const templateBuilder = this._getTemplateBuilder(ctx);

    return switchMap((templateInput) =>
      isNonEmptyString(templateInput) &&
      templateExpressionRegex.test(templateInput)
        ? of(templateInput).pipe(
            interpolateTemplate((part) => templateBuilder.interpolate(part)),
            filter((templateOutput) => templateOutput !== templateInput),
            // Ignore errors for now
            catchError((e) => EMPTY),
          )
        : EMPTY,
    );
  }

  /**
   * Helper to get the template builder from context.
   * @param ctx
   * @returns
   */
  private _getTemplateBuilder(ctx: GraphBuilderContext): TemplateBuilder {
    return ctx.injector.get(TemplateBuilder);
  }
}
